import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardContent, CircularProgress, Divider, Fab, FormControl, FormControlLabel, Grid, InputAdornment, Radio, RadioGroup, TextField, Tooltip, Typography } from '@material-ui/core'
import { Page, Header } from 'components'
import { useExameIdentificacaoCriminalService } from 'hooks/useExameIdentificacaoCriminalService'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import useRouter from 'utils/useRouter'
import { v4 as uuid } from 'uuid'
import { useFormState } from 'hooks/useFormState'
import { BackButton } from 'components/BackButton/BackButton'
import { ButtonWithLoading } from 'components/ButtonWithLoading'
import AddIcon from '@material-ui/icons/Add'
import useStyles from './styles'
import { useParams } from 'react-router'

const schema = {
  numeroProtocolo: {
    presence: { allowEmpty: false, message: '^Número do protocolo ABIS é obrigatório' },
  },
}

export const ExameIdentificacaoCriminal = (props) => {
  const { route } = props
  const classes = useStyles()
  const router = useRouter()
  const params = useParams()
  const exameIdentificacaoCriminalService = useExameIdentificacaoCriminalService()
  const exameRealizadoService = useExameRealizadoService()
  const [exameIdentificacaoCriminal, setExameIdentificacaoCriminal] = useState(false)
  const [processandoExame, setProcessandoExame] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [isFormError, setIsFormError] = useState(false)

  const formExame = useFormState(schema, {
    inqueritoPolicial: null,
    numeroProtocolo: null,
    nome: null,
    alcunha: null,
    filiacoes: [
      { uuid: uuid(), nome: null },
      { uuid: uuid(), nome: null },
    ],
    sexo: null,
    cpf: null,
    rg: null,
    naturalidade: null,
    nacionalidade: null,
  })

  const fetchExameIdentificacaoCriminal = useCallback(async () => {
    if (params.id) {
      const response = await exameIdentificacaoCriminalService.listarExameIdentificacaoCriminalParaExaminadorAsync(params.id, setProcessandoExame)
      setExameIdentificacaoCriminal(response)
    }
  }, [params.id])

  const cpfMask = useCallback(
    (value) =>
      value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1'),
    []
  )

  const getValorFiliacaoPeloUuid = useCallback(
    (uuid) => {
      let result = null
      formExame.formState.values.filiacoes.forEach((f) => {
        if (f.uuid === uuid) result = f.nome
      })
      return result
    },
    [formExame]
  )

  const handleChangeFiliacaoPeloUuid = useCallback((uuid, valor) => {
    formExame.setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        filiacoes: prevState.values.filiacoes.map((f) => (f.uuid === uuid ? { ...f, nome: valor } : f)),
      },
    }))
  }, [])

  const handleAdicionarFiliacaoPelloUuidRegistro = useCallback(() => {
    formExame.setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        filiacoes: [...formExame.formState.values.filiacoes, { uuid: uuid(), nome: null }],
      },
    }))
  }, [])

  const handleClickBotaoFinalizar = useCallback(async () => {
    if (!isFormError) {
      await exameIdentificacaoCriminalService.confirmarDadosExameIdentificacaoCriminal(params.id, exameIdentificacaoCriminal?.exReal?.exSol?.perics[0].id, formExame.formState.values, setProcessandoOperacao)
      await exameRealizadoService.gerarDocumento(params.id, setProcessandoOperacao)
      router.history.push({
        pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${params.id}`,
        state: { IgnoneUploadImagem: true },
      })
    }
  }, [formExame, params.id, isFormError, exameIdentificacaoCriminal])

  useEffect(() => {
    fetchExameIdentificacaoCriminal()
  }, [fetchExameIdentificacaoCriminal])

  useEffect(() => {
    if (exameIdentificacaoCriminal?.id) {
      formExame.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          inqueritoPolicial: exameIdentificacaoCriminal.inqueritoPolicial,
          numeroProtocolo: exameIdentificacaoCriminal.exReal?.regIdents[0]?.numeroProtocolo,
          nome: exameIdentificacaoCriminal.exReal?.regIdents[0]?.nome,
          alcunha: exameIdentificacaoCriminal.exReal?.regIdents[0]?.alcunha,
          filiacoes: exameIdentificacaoCriminal.exReal?.regIdents[0]?.filiacoes?.map((filiacao) => ({ uuid: uuid(), filiacao })) || [
            { uuid: uuid(), nome: null },
            { uuid: uuid(), nome: null },
          ],
          sexo: exameIdentificacaoCriminal.exReal?.regIdents[0]?.sexo,
          cpf: exameIdentificacaoCriminal.exReal?.regIdents[0]?.cpf,
          rg: exameIdentificacaoCriminal.exReal?.regIdents[0]?.rg,
          naturalidade: exameIdentificacaoCriminal.exReal?.regIdents[0]?.naturalidade,
          nacionalidade: exameIdentificacaoCriminal.exReal?.regIdents[0]?.nacionalidade,
        },
      }))
    }
  }, [exameIdentificacaoCriminal])

  useEffect(() => {
    if (formExame.hasErrorWithoutTouch('numeroProtocolo')) {
      setIsFormError(true)
    } else {
      setIsFormError(false)
    }
  }, [formExame])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} />
      <Card className={classes.results}>
        <CardContent>
          {processandoExame ? (
            <div className={classes.carregando}>
              <CircularProgress disableShrink />
            </div>
          ) : (
            <Grid container md={12} sm={12} xs={12}>
              <Grid item>
                <Typography>
                  <b>DADOS DO EXAME</b>
                </Typography>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <Divider className={classes.dividerTitle} />
              </Grid>
              <Grid item container justifyContent="space-between" alignItems="center" className={classes.fieldGroup} spacing={3}>
                <Grid className={classes.item} item md={12} sm={12} xs={12}>
                  <TextField
                    fullWidth
                    label="NÚMERO DO PROTOCOLO ABIS"
                    name="numeroProtocolo"
                    required
                    onChange={formExame.handleChange}
                    value={formExame.formState.values.numeroProtocolo}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid className={classes.item} item md={12} sm={12} xs={12}>
                  <TextField
                    fullWidth
                    label="INQUÉRITO POLICIAL"
                    name="inqueritoPolicial"
                    onChange={formExame.handleChange}
                    value={formExame.formState.values.inqueritoPolicial}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item container md={12} sm={12} xs={12} spacing={1}>
                  <Grid className={classes.item} item md={3} sm={6} xs={12}>
                    <TextField
                      fullWidth
                      label="CPF"
                      name="cpf"
                      onChange={(e) => formExame.handleChangeWithModification(e, cpfMask)}
                      value={formExame.formState.values.cpf}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid className={classes.item} item md={3} sm={6} xs={12}>
                    <TextField
                      fullWidth
                      label="RG"
                      name="rg"
                      onChange={formExame.handleChange}
                      value={formExame.formState.values.rg}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid className={classes.item} item md={12} sm={12} xs={12}>
                  <TextField
                    fullWidth
                    label="NOME"
                    name="nome"
                    onChange={formExame.handleChange}
                    value={formExame.formState.values.nome}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid className={classes.item} item md={12} sm={12} xs={12}>
                  <TextField
                    fullWidth
                    label="ALCUNHA"
                    name="alcunha"
                    onChange={formExame.handleChange}
                    value={formExame.formState.values.alcunha}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item container md={12} sm={12} xs={12} spacing={1}>
                  {formExame.formState.values.filiacoes?.map((filiacao, index) => (
                    <Grid item md={12} sm={12} xs={12} className={classes.filiacao}>
                      <TextField
                        value={getValorFiliacaoPeloUuid(filiacao.uuid)}
                        variant="outlined"
                        label="FILIAÇÃO"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        name={`filiacao${filiacao.uuid}`}
                        onChange={(event) => handleChangeFiliacaoPeloUuid(filiacao.uuid, event.target.value)}
                        InputProps={{
                          endAdornment: index + 1 === formExame.formState.values.filiacoes?.length && (
                            <InputAdornment position="start">
                              <Tooltip title="Adicionar filiação">
                                <Fab color="primary" size="small" onClick={() => handleAdicionarFiliacaoPelloUuidRegistro(filiacao.uuid)} className={classes.botaoAddFiliacao}>
                                  <AddIcon />
                                </Fab>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Grid item container md={12} sm={12} xs={12}>
                  <Grid item md={12} sm={12} xs={12}>
                    <Typography variant="body2">SEXO</Typography>
                  </Grid>
                  <Grid item md={12} sm={12} xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup aria-label="sexo" name="sexo" value={formExame.formState.values.sexo} onChange={formExame.handleChange} style={{ display: 'flex', flexDirection: 'row' }}>
                        <FormControlLabel value="MASCULINO" control={<Radio />} label={<Typography variant="body2">MASCULINO</Typography>} />
                        <FormControlLabel value="FEMININO" control={<Radio />} label={<Typography variant="body2">FEMININO</Typography>} />
                        <FormControlLabel value="DESCONHECIDO" control={<Radio />} label={<Typography variant="body2">DESCONHECIDO</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <TextField
                    variant="outlined"
                    label="NACIONALIDADE"
                    name="nacionalidade"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={formExame.handleChange}
                    value={formExame.formState.values.nacionalidade}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <TextField
                    variant="outlined"
                    label="NATURALIDADE"
                    name="naturalidade"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={formExame.handleChange}
                    value={formExame.formState.values.naturalidade}
                  />
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                  <Divider />
                </Grid>
                <Grid item container md={12} sm={12} xs={12} justifyContent="space-between">
                  <Grid item>
                    <BackButton size="small" />
                  </Grid>
                  <Grid item>
                    <ButtonWithLoading color="primary" isDisabled={isFormError} isLoading={processandoOperacao} variant="contained" label="Finalizar" size="large" onClick={() => handleClickBotaoFinalizar()} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}
